import React, { useEffect, useMemo, useState } from "react";
import "./DoARAsync.css";

const SIZE_CLASS = {
  small: "is-small",
  medium: "is-medium",
  large: "is-large",
};

function clampProgress(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(100, Math.max(0, Math.round(number)));
}

export function DoARPulse({ size = "medium", className = "" }) {
  return (
    <span
      className={`doar-pulse ${SIZE_CLASS[size] || SIZE_CLASS.medium} ${className}`.trim()}
      aria-hidden="true"
    >
      <span className="doar-pulse__orbit" />
      <span className="doar-pulse__core" />
    </span>
  );
}

export function AsyncStatus({
  label = "Working…",
  detail = "",
  progress = null,
  compact = false,
  className = "",
}) {
  const pct = useMemo(() => clampProgress(progress), [progress]);
  return (
    <div
      className={`doar-async-status${compact ? " is-compact" : ""} ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <DoARPulse size={compact ? "small" : "medium"} />
      <div className="doar-async-status__copy">
        <strong>{label}</strong>
        {detail ? <span>{detail}</span> : null}
        {pct !== null ? (
          <div
            className="doar-async-progress"
            role="progressbar"
            aria-label={label}
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={pct}
          >
            <span style={{ width: `${pct}%` }} />
          </div>
        ) : null}
      </div>
      {pct !== null ? <span className="doar-async-status__pct">{pct}%</span> : null}
    </div>
  );
}

export function SurfaceLoader({
  title = "DoAR",
  detail = "Opening…",
  slowDetail = "",
  slowAfterMs = 7000,
  progress = null,
  onRetry = null,
  homeHref = null,
  className = "",
}) {
  const [slow, setSlow] = useState(false);
  const pct = useMemo(() => clampProgress(progress), [progress]);

  useEffect(() => {
    if (!slowAfterMs || slowAfterMs < 1) return undefined;
    const timer = window.setTimeout(() => setSlow(true), slowAfterMs);
    return () => window.clearTimeout(timer);
  }, [slowAfterMs]);

  return (
    <main
      className={`doar-surface-loader ${className}`.trim()}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <DoARPulse size="large" />
      <strong>{title}</strong>
      <p>{slow && slowDetail ? slowDetail : detail}</p>
      {pct !== null ? (
        <div
          className="doar-surface-loader__progress"
          role="progressbar"
          aria-label={detail}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={pct}
        >
          <span style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {slow && (onRetry || homeHref) ? (
        <div className="doar-surface-loader__actions">
          {onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}
          {homeHref ? <a href={homeHref}>Go home</a> : null}
        </div>
      ) : null}
    </main>
  );
}

export default SurfaceLoader;
